import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "~/components/Button";
import { Input } from "~/components/Controls/Input";
import { useConfirm, useMessage } from "~/components/Dialogs";
import { IntentText } from "~/components/Form";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByString } from "~/lib/sort";
import { clientsLinks } from "~/modules/Clients";
import { createAccountManager, deleteAccountManager, updateAccountManager } from "~/modules/Clients/api";
import { IAccountManager } from "~/modules/Clients/models/AccountManager";
import { DictionaryUsageLinks } from "~/modules/Dictionary/components/DictionaryUsageLinks";
import { ACCOUNT_MANAGERS_QUERY_KEY, useAccountManagersQuery } from "~/modules/Dictionary/queries";
import style from "./AccountManagersTab.module.scss";

const getErrorMessage = (error: any): string => error?.response?.data?.message || error?.message || "Unknown error";

type FormMode = "idle" | "create" | "edit";

export const AccountManagersTab = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const message = useMessage();

  const { data: items = [], isPending, isFetching, isError, error, refetch } = useAccountManagersQuery();

  const [mode, setMode] = useState<FormMode>("idle");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createAccountManager({ name: name.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_MANAGERS_QUERY_KEY });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => updateAccountManager(editingId!, { name: name.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_MANAGERS_QUERY_KEY });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: IAccountManager["id"]) => deleteAccountManager(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_MANAGERS_QUERY_KEY });
    },
  });

  const normalizedItems = useMemo(() => items ?? [], [items]);
  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isFormOpen = mode !== "idle";
  const isEditing = mode === "edit";
  const canSubmit = name.trim().length > 0;

  const resetForm = () => {
    setFormError(null);
    setName("");
    setEditingId(null);
    setMode("idle");
  };

  const handleOpenAdd = () => {
    setFormError(null);
    setName("");
    setEditingId(null);
    setMode("create");
  };

  const handleEdit = (item: IAccountManager) => {
    setFormError(null);
    setName(item.name);
    setEditingId(item.id);
    setMode("edit");
  };

  const handleCancel = () => resetForm();

  const handleSave = () => {
    setFormError(null);
    const mutation = isEditing ? updateMutation : createMutation;
    mutation.mutate(undefined, {
      onError: (e: any) => {
        const msg = getErrorMessage(e);
        setFormError(msg);
        message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
      },
    });
  };

  const handleDelete = (item: IAccountManager) =>
    confirm(`Delete account manager "${item.name}"?`, {
      icon: <i className="fa fa-question" />,
      intent: Intent.DANGER,
      onOk: () => {
        deleteMutation.mutate(item.id, {
          onError: (e: any) => {
            message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
          },
        });
      },
    });

  return (
    <div>
      <div className={style.toolbar}>
        <div className={style.toolbarLeft}>
          {isFormOpen && (
            <Input
              className={style.field}
              placeholder="Name"
              value={name}
              onChange={setName}
              disabled={isBusy}
              autoFocus
            />
          )}
          {!isFormOpen && <DictionaryUsageLinks links={[{ label: "Sellers", to: clientsLinks.sellers() }]} />}
        </div>
        <div className={style.toolbarRight}>
          {!isFormOpen ? (
            <Button
              responsive
              intent={Intent.PRIMARY}
              disabled={isBusy}
              onClick={handleOpenAdd}
              leftIcon={<i className="fa fa-plus" />}
            >
              Add
            </Button>
          ) : (
            <>
              <Button
                responsive
                intent={Intent.PRIMARY}
                disabled={!canSubmit || isBusy}
                onClick={handleSave}
                leftIcon={<i className="fa fa-check" />}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
              <Button responsive disabled={isBusy} onClick={handleCancel} leftIcon={<i className="fa fa-times" />}>
                Cancel
              </Button>
            </>
          )}
          <Button
            responsive
            intent={Intent.SUCCESS}
            disabled={isFetching}
            onClick={() => refetch()}
            leftIcon={<i className="fa fa-refresh" />}
          >
            Reload
          </Button>
        </div>
      </div>

      {isFormOpen && formError && <IntentText className={style.error} intent={Intent.DANGER}>{formError}</IntentText>}
      {isError && <IntentText className={style.error} intent={Intent.DANGER}>{getErrorMessage(error)}</IntentText>}

      <div className={style.tableWrap}>
        {isPending && normalizedItems.length === 0 && <Progress message="Loading..." />}
        {!isPending && normalizedItems.length === 0 && <div className={style.emptyState}>No account managers.</div>}
        {normalizedItems.length > 0 && (
          <TableSort
            records={normalizedItems}
            header
            columns={[
              { header: "Name", field: "name", sort: sortByString, cellAttrs: { className: style.nameCell } },
              {
                short: true,
                cell: (m: IAccountManager) => (
                  <TableRowActions>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.PRIMARY}
                      disabled={isBusy}
                      onClick={() => handleEdit(m)}
                      leftIcon={<i className="fa fa-edit" />}
                    >
                      Edit
                    </Button>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.DANGER}
                      disabled={isBusy}
                      onClick={() => handleDelete(m)}
                      leftIcon={<i className="fa fa-trash" />}
                    >
                      Delete
                    </Button>
                  </TableRowActions>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};
